import React, { useRef } from "react";
import placeholderProfileImage from "../../assets/images/profile_image_placeholder.png";
import { CONSTANTS } from "../../constants/Constants";
import Tags from "../Tags";
import VideoCard from "../VideoCard";
import QAaccordian from "../QAaccordian";
import UserJourney from "../UserJourney";
import Button from "../Buttons";
import moment from "moment";
import Breadcrumb from "../BreadCrumb";
import ProfileDetailCard from "../ProfileDetailCard";
import ProfileRecommendationCard from "../ProfileRecommendationCard";
import Carousel from "../Carousel";
import styles from './ProfilePreview.module.css'

interface ProfilePreviewProps {
  currentProfile: any;
  clickHandler?: any;
  showBckBtn?: boolean;
  btnTitle?: string;
  showBreadCrumbs: boolean;
  showHeading: boolean;
}

const ProfilePreview: React.FC<ProfilePreviewProps> = ({
  currentProfile,
  clickHandler,
  showBckBtn,
  btnTitle,
  showBreadCrumbs,
  showHeading,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleRecommendationClick = (route: string) => {
    window.open(route, '_blank');
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.dataset.isDragging = "true";
    container.dataset.startX = `${e.pageX - container.offsetLeft}`;
    container.dataset.scrollLeft = `${container.scrollLeft}`;
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = scrollContainerRef.current;
    if (!container || container.dataset.isDragging !== "true") return;

    e.preventDefault();
    const startX = parseFloat(container.dataset.startX || "0");
    const scrollLeft = parseFloat(container.dataset.scrollLeft || "0");
    const x = e.pageX - container.offsetLeft;
    const walk = x - startX; // Distance moved
    container.scrollLeft = scrollLeft - walk; // Adjust scroll
  };

  const handleMouseUpOrLeave = () => {
    const container = scrollContainerRef.current;
    if (container) container.dataset.isDragging = "false";
  };

  return (
    <>
      <div className="break-all gap-10 md:flex min-h-screen m-auto relative layout-container mb-8">
        <div className="md:w-72 sm:w-full">
          {showBreadCrumbs ? (
            <div>
              <Breadcrumb
                path={
                  showHeading
                    ? [
                      { name: CONSTANTS.DASHBOARD_TXT, url: "/dashboard" },
                      { name: CONSTANTS.PROFILE_TXT, url: "/profile-detail" },
                    ]
                    : [{ name: CONSTANTS.PROFILE_DETAIL_TXT, url: "" }]
                }
              />
            </div>
          ) : (
            <div className="mt-4">{CONSTANTS.PROFILE_PREVIEW_TXT}</div>
          )}

          <ProfileDetailCard
            name={currentProfile?.name || CONSTANTS.NO_DATA_AVAILABLE}
            job_description={currentProfile?.description}
            title={currentProfile?.title}
            availability_status={currentProfile?.availabilityStatus}
            availableFrom={currentProfile?.availableFrom}
            image_url={currentProfile?.imageUrl || placeholderProfileImage}
            location={
              (currentProfile?.city || currentProfile?.country) &&
              `${currentProfile?.city || ""}, ${currentProfile?.country || ""}`
            }
          />
        </div>
        {/* =================== */}

        <div className="md:w-[calc(100%-300px-1.5rem)] sm:w-[100%]">
          {showBckBtn && (
            <div className="w-full flex justify-end">
              <div className="w-32 mt-4 hidden md:block">
                <Button
                  btn_label={btnTitle || ""}
                  on_click_handler={clickHandler}
                />
              </div>
            </div>
          )}

          <div className="pt-4">
            {Array.isArray(currentProfile?.resourceTags) &&
              currentProfile?.resourceTags.length > 0 && (
                <>
                  <h3 className="text-[17px] font-medium md:font-normal">
                    {CONSTANTS.TECHSTACK_HEADING_TXT}
                  </h3>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {currentProfile?.resourceTags.map((tags: any, index: number) => (
                      <Tags key={index} tags={tags?.name} />
                    ))}
                  </div>
                </>
              )
            }
          </div>

          {/* Videos */}
          {Array.isArray(currentProfile?.resourceMedia) &&
            currentProfile.resourceMedia.filter(
              (media: any) => media.status?.toLowerCase() !== "disabled"
            ).length > 0 &&
            (
              <>
                <div className="pt-4 pb-2 md:hidden">
                  <hr className="h-[10px] bg-[#F5F6F7] w-screen left-0 absolute border-0" />
                </div>

                <div className="mt-5">
                  <h3 className="text-[17px]">
                    {CONSTANTS.MEET_TXT} {currentProfile?.name}
                  </h3>
                </div>

                <div
                  ref={scrollContainerRef}
                  className={`${styles.customScrollbar} w-full flex gap-3 mt-3 overflow-x-auto whitespace-nowrap cursor-grab`}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUpOrLeave}
                  onMouseLeave={handleMouseUpOrLeave}
                >
                  {
                    currentProfile.resourceMedia
                      ?.filter(
                        (media: any) => media.status?.toLowerCase() !== "disabled"
                      )
                      .sort(
                        (a: any, b: any) => (a.position ?? 0) - (b.position ?? 0)
                      )
                      .map((video: any, index: number) => (
                        <VideoCard key={index} {...video} />
                      ))
                  }
                </div>
              </>
            )
          }

          {/* Questions & Answers */}
          {Array.isArray(currentProfile?.resourceQuestions) &&
            currentProfile?.resourceQuestions?.filter((qa: any) => qa.status !== CONSTANTS.ENABLED_TXT.toLocaleLowerCase()).length > 0 && (
              <>
                <div className="pt-4 pb-2 md:hidden">
                  <hr className="h-[10px] bg-[#F5F6F7] w-screen left-0 absolute border-0" />
                </div>
                <div className="w-full h-fit mt-6">
                  <h2 className="text-[17px] font-medium md:font-normal">
                    {CONSTANTS.QNA_TXT}
                  </h2>
                  <QAaccordian
                    questionsAndAnswers={currentProfile?.resourceQuestions}
                    showBorder={true}
                  />
                </div>
              </>
            )
          }

          {Array.isArray(currentProfile?.resourceExperience) &&
            currentProfile?.resourceExperience?.filter((exp: any) => exp.status !== CONSTANTS.ENABLED_TXT.toLocaleLowerCase()).length > 0 && (
              <>
                <div className="pt-4 pb-2 md:hidden">
                  <hr className="h-[10px] bg-[#f7f6f5] w-screen left-0 absolute border-0" />
                </div>

                <div className="w-full h-fit mt-7">
                  <h2 className="text-[17px] font-medium md:font-normal">{CONSTANTS.EXPERIENCE_HEADING_TXT}</h2>

                  {
                    currentProfile.resourceExperience?.filter(
                      (experience: any) =>
                        experience.status?.toLowerCase() !== "disabled"
                    ).map(
                      (data: any, index: number, exp: any) => (
                        <UserJourney
                          key={index}
                          logo_url={data.imageUrl}
                          organization={data.organizationName}
                          title={data.title}
                          location={`${data.city ? data.city : ""}${data.country ? `, ${data.country} |` : ""
                            }
                        ${data.startedOn
                              ? ` ${moment(data.startedOn).format("MMM YYYY")}`
                              : ""
                            }
                        ${data.endedOn
                              ? ` - ${moment(data.endedOn).format("MMM YYYY")}`
                              : " - Present"
                            }
                          `}
                          showBorder={
                            index === currentProfile.resourceExperience.length - 1
                          }
                        />
                      )
                    )
                  }
                </div>
              </>
            )}

          {Array.isArray(currentProfile?.resourceEducations) &&
            currentProfile?.resourceEducations?.filter((edu: any) => edu.status !== CONSTANTS.ENABLED_TXT.toLocaleLowerCase()).length > 0 && (
              <>
                <div className="pt-4 pb-2 md:hidden">
                  <hr className="h-[10px] bg-[#F5F6F7] w-screen left-0 absolute border-0" />
                </div>

                <div className="w-full h-fit mt-7">
                  <h2 className="text-[17px] font-medium md:font-normal">{CONSTANTS.EDUCATION_HEADING_TXT}</h2>
                  {
                    currentProfile.resourceEducations?.filter(
                      (education: any) => education.status?.toLowerCase() !== "disabled"
                    ).map(
                      (data: any, index: number) => (
                        <UserJourney
                          key={index}
                          logo_url={data.imageUrl}
                          organization={data.institute}
                          title={data.degree}
                          location={`${data.city ? data.city : ""}${data.country ? `, ${data.country} |` : ""
                            }
                          ${data.startedOn
                              ? ` ${moment(data.startedOn).format("YYYY")}`
                              : ""
                            }
                            ${data.endedOn
                              ? ` - ${moment(data.endedOn).format("YYYY")}`
                              : " - Present"
                            }
                          `}
                          showBorder={
                            index === currentProfile.resourceEducations.length - 1
                          }
                        />
                      )
                    )
                  }
                </div>
              </>
            )
          }

          {Array.isArray(currentProfile?.resourceSkills) &&
            currentProfile?.resourceSkills.length > 0 && (
              <>
                <div className="pt-4 pb-2 md:hidden">
                  <hr className="h-[10px] bg-[#F5F6F7] w-screen left-0 absolute border-0" />
                </div>

                <div className="pt-6">
                  <h2 className="text-[17px] font-medium md:font-normal">{CONSTANTS.SKILLS_HEADING_TXT}</h2>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {
                      currentProfile.resourceSkills.map(
                        (skillset: any, index: number) => (
                          <Tags key={index} tags={skillset?.name} rating={skillset?.rating} />
                        )
                      )
                    }
                  </div>
                </div>
              </>
            )}

          {/* Recommendations Section */}
          {currentProfile?.suggestedResources?.length > 0 && (
            <>
              <div className="pt-4 pb-2 md:hidden">
                <hr className="h-[10px] bg-[#F5F6F7] w-screen left-0 absolute border-0" />
              </div>

              <div className="pt-6">
                <h2 className="text-[17px] font-medium md:font-normal">
                  {CONSTANTS.RECOMMENDATIONS_TXT}{" "}
                  <span className="hidden md:inline">{CONSTANTS.YOU_MIGHT_ALSO_LIKE_TXT}</span>
                </h2>
                <Carousel className="mt-4">
                  {currentProfile?.suggestedResources?.map((recommendation: any, index: number) => (
                    <ProfileRecommendationCard
                      key={index}
                      data={{ ...recommendation, onClick: handleRecommendationClick }}
                    />
                  ))}
                </Carousel>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ProfilePreview;
